import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import Badge from '../../components/ui/Badge';
import { Callout } from '../../components/ui/Callout';
import { Button } from '../../components/ui';
import CommentPromptDialog, {
  type CommentPromptActionKind,
} from '../../components/ui/CommentPromptDialog';
import ConfirmationDialog from '../../components/ui/ConfirmationDialog';
import { ChevronLeftIcon, DocumentTextIcon, SpinnerIcon } from '../../components/ui/Icons';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useTopbar } from '../../contexts/TopbarContext';
import { useNavigateBack, getLocationReturnPath } from '../../hooks/useNavigateBack';
import { cn } from '../../lib/utils';
import {
  approveApprovalStep,
  cancelApproval,
  deleteApproval,
  rejectApprovalStep,
  submitApproval,
} from '../../services/mutations';
import { useDocument } from '../documents/useData';
import { ApprovalFlowPreview } from './ApprovalFlowPreview';
import { ApprovalStatus, useApproval, useApprovalDelegations } from './useData';
import { approvalSourcePath } from './sourceLinks';

const variants: Record<ApprovalStatus, 'default' | 'warning' | 'success' | 'danger' | 'info'> = {
  DRAFT: 'default',
  PENDING: 'warning',
  APPROVED: 'success',
  REJECTED: 'danger',
  CANCELLED: 'info',
};

const statusLabel: Record<ApprovalStatus, string> = {
  DRAFT: 'Draft',
  PENDING: 'Pending',
  APPROVED: 'Approved',
  REJECTED: 'Rejected',
  CANCELLED: 'Cancelled',
};

type LastReturnPayload = {
  comments?: string | null;
  at?: string | null;
  byUserId?: number | null;
  byUserName?: string | null;
  label?: string | null;
};

type ActionHistoryEntry = {
  action: 'CREATED' | 'SUBMITTED' | 'APPROVED' | 'REJECTED' | 'RETURNED' | 'CANCELLED';
  at: string;
  byUserId: number;
  byUserName?: string | null;
  stepId?: number;
  stage?: number;
  label?: string | null;
  comments?: string | null;
};

const ACTION_HISTORY_LABEL: Record<ActionHistoryEntry['action'], string> = {
  CREATED: 'Created',
  SUBMITTED: 'Submitted',
  APPROVED: 'Approved',
  REJECTED: 'Rejected',
  RETURNED: 'Returned for changes',
  CANCELLED: 'Cancelled',
};

const ACTION_DOT: Record<ActionHistoryEntry['action'], string> = {
  CREATED: 'bg-slate-400',
  SUBMITTED: 'bg-sky-500',
  APPROVED: 'bg-emerald-500',
  REJECTED: 'bg-red-500',
  RETURNED: 'bg-amber-500',
  CANCELLED: 'bg-slate-400',
};

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, unknown>) : null;
}

function readLastReturn(payload: unknown): LastReturnPayload | null {
  const record = asRecord(payload);
  const raw = record?._lastReturn;
  const last = asRecord(raw);
  if (!last) return null;
  return last as LastReturnPayload;
}

/** Activity trail from payload `_actionHistory` only (not `_lastReturn`). */
function buildClientActionHistory(approval: {
  payload?: Record<string, unknown> | null;
  actionHistory?: ActionHistoryEntry[] | null;
}): ActionHistoryEntry[] {
  const entries: ActionHistoryEntry[] = [];
  const seen = new Set<string>();
  const push = (entry: ActionHistoryEntry) => {
    const key = `${entry.action}|${entry.at}|${entry.stepId ?? ''}|${entry.comments ?? ''}`;
    if (seen.has(key)) return;
    seen.add(key);
    entries.push(entry);
  };

  const payload = asRecord(approval.payload);
  const rawHistory = Array.isArray(payload?._actionHistory) ? payload!._actionHistory : [];
  for (const item of rawHistory) {
    const row = asRecord(item);
    if (!row || typeof row.action !== 'string' || typeof row.at !== 'string') continue;
    if (!['CREATED', 'SUBMITTED', 'APPROVED', 'REJECTED', 'RETURNED', 'CANCELLED'].includes(row.action)) {
      continue;
    }
    push({
      action: row.action as ActionHistoryEntry['action'],
      at: row.at,
      byUserId: typeof row.byUserId === 'number' ? row.byUserId : 0,
      byUserName: typeof row.byUserName === 'string' ? row.byUserName : null,
      stepId: typeof row.stepId === 'number' ? row.stepId : undefined,
      stage: typeof row.stage === 'number' ? row.stage : undefined,
      label: typeof row.label === 'string' ? row.label : null,
      comments: typeof row.comments === 'string' ? row.comments : null,
    });
  }

  // Fallback when payload history is empty but API already normalized `_actionHistory`.
  if (entries.length === 0 && Array.isArray(approval.actionHistory)) {
    for (const entry of approval.actionHistory) {
      if (!entry?.action || !entry.at) continue;
      push(entry);
    }
  }

  return entries.sort((a, b) => new Date(b.at).getTime() - new Date(a.at).getTime());
}

function formatWhen(value?: string | null) {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleString(undefined, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

const MetaChip: React.FC<{ label: string; value: string }> = ({ label, value }) => {
  const { colors } = useTheme();
  return (
    <span className={cn('inline-flex max-w-full items-center gap-1.5 rounded-full border px-2.5 py-1', colors.border)}>
      <span className={cn('shrink-0 font-medium', colors.textMuted)}>{label}</span>
      <span className={cn('truncate', colors.text)}>{value}</span>
    </span>
  );
};

function amountOf(value: unknown): number {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim() && Number.isFinite(Number(value))) return Number(value);
  return 0;
}

/** Read-only preview of the originating document (invoice fields + journal lines) for `sourceModule === 'documents'`. */
const DocumentSourcePreview: React.FC<{ documentId: number }> = ({ documentId }) => {
  const { colors } = useTheme();
  const { data: doc, isLoading } = useDocument(documentId);
  const payload = doc?.payload || {};
  const lines = useMemo(() => doc?.lines || [], [doc?.lines]);

  if (isLoading) {
    return (
      <div className={cn('flex items-center gap-2 rounded-2xl border px-5 py-4 text-sm shadow-sm', colors.surface, colors.border, colors.textMuted)}>
        <SpinnerIcon className="h-4 w-4" />
        Loading source document…
      </div>
    );
  }
  if (!doc) return null;

  return (
    <section className={cn('overflow-hidden rounded-2xl border shadow-sm', colors.surface, colors.border)}>
      <div className={cn('flex flex-wrap items-center justify-between gap-3 border-b px-5 py-3.5', colors.border)}>
        <div>
          <p className={cn('text-xs font-semibold uppercase tracking-wide', colors.textMuted)}>Source document</p>
          <p className={cn('mt-0.5 text-sm font-semibold', colors.text)}>{doc.title}</p>
        </div>
        <Link
          to={`/documents/${documentId}`}
          className="text-sm font-medium text-sky-700 hover:underline dark:text-sky-300"
        >
          Open document →
        </Link>
      </div>
      <div className="grid divide-y sm:grid-cols-3 sm:divide-x sm:divide-y-0 dark:divide-slate-800">
        {(
          [
            { key: 'netAmount' as const, label: 'Net' },
            { key: 'vatAmount' as const, label: 'VAT' },
            { key: 'totalAmount' as const, label: 'Payable' },
          ] as const
        ).map((item) => (
          <div key={item.key} className="px-4 py-3">
            <p className={cn('text-[11px] font-semibold uppercase tracking-wide', colors.textMuted)}>{item.label}</p>
            <p className={cn('mt-1 text-lg font-semibold tabular-nums', colors.text)}>
              {amountOf(payload[item.key]).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </p>
          </div>
        ))}
      </div>
      {lines.length > 0 && (
        <div className="overflow-x-auto border-t" style={{ borderColor: 'inherit' }}>
          <table className="w-full min-w-[480px] text-sm">
            <thead className={cn('border-b text-left text-[11px] uppercase tracking-wide', colors.border, colors.textMuted)}>
              <tr>
                <th className="px-5 py-2 font-medium">Type</th>
                <th className="px-3 py-2 font-medium">Narrative</th>
                <th className="px-3 py-2 text-right font-medium">Debit</th>
                <th className="px-3 py-2 text-right font-medium">Credit</th>
              </tr>
            </thead>
            <tbody>
              {lines.map((line, index) => (
                <tr key={line.id ?? index} className={cn('border-b last:border-b-0', colors.border)}>
                  <td className={cn('px-5 py-2', colors.text)}>{line.role || '—'}</td>
                  <td className={cn('px-3 py-2', colors.textMuted)}>{line.narrative || '—'}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{amountOf(line.debitLocal).toFixed(2)}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{amountOf(line.creditLocal).toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
};

export const Detail: React.FC = () => {
  const id = Number(useParams<{ id: string }>().id);
  const location = useLocation();
  const goBack = useNavigateBack('/approvals', getLocationReturnPath(location.state));
  const { data: approval, isLoading, mutate } = useApproval(id);
  const { data: delegations = [] } = useApprovalDelegations();
  const { user } = useAuth();
  const { colors } = useTheme();
  const { setTitleOverride, setRightActions } = useTopbar();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [commentPrompt, setCommentPrompt] = useState<{
    mode: 'step' | 'submit';
    stepId?: number;
    action?: 'approve' | 'reject';
    kind: CommentPromptActionKind;
  } | null>(null);

  const activeDelegationsToMe = useMemo(() => {
    const now = Date.now();
    return delegations.filter(
      (d) =>
        d.delegateUserId === user?.id &&
        d.isActive &&
        (!d.validTo || new Date(d.validTo).getTime() >= now),
    );
  }, [delegations, user?.id]);

  const actingForName = (step: NonNullable<NonNullable<typeof approval>['steps']>[number]) => {
    if (!user || !step.assigneeUserId || step.assigneeUserId === user.id) return null;
    const delegation = activeDelegationsToMe.find((d) => d.userId === step.assigneeUserId);
    if (!delegation) return null;
    const name =
      step.assigneeUser?.userDisplayName ||
      [step.assigneeUser?.userFirstName, step.assigneeUser?.userLastName].filter(Boolean).join(' ') ||
      delegation.user?.userDisplayName ||
      [delegation.user?.userFirstName, delegation.user?.userLastName].filter(Boolean).join(' ') ||
      'colleague';
    return name;
  };

  useEffect(() => {
    setTitleOverride(approval?.title || (Number.isFinite(id) ? `Approval #${id}` : 'Approval'));
    return () => {
      setTitleOverride(undefined);
      setRightActions(null);
    };
  }, [approval?.title, id, setTitleOverride, setRightActions]);

  useEffect(() => {
    if (!approval) {
      setRightActions(null);
      return;
    }
    setRightActions(<Badge variant={variants[approval.status]}>{statusLabel[approval.status]}</Badge>);
    return () => setRightActions(null);
  }, [approval, setRightActions]);

  if (isLoading) {
    return (
      <div className={cn('flex flex-col items-center justify-center gap-3 p-16', colors.textMuted)}>
        <SpinnerIcon className="h-6 w-6" />
        <p className="text-sm">Loading approval…</p>
      </div>
    );
  }

  if (!approval) {
    return (
      <div className="p-6">
        <p className={cn('text-sm', colors.textMuted)}>Approval not found.</p>
        <button type="button" onClick={goBack} className="mt-3 inline-block text-sm text-indigo-600 hover:underline">
          ← Back to inbox
        </button>
      </div>
    );
  }

  const errMsg = (e: any) =>
    (Array.isArray(e?.response?.data?.message) ? e.response.data.message.join(', ') : e?.response?.data?.message) ||
    e?.message ||
    'Request failed';

  const isAssigned = (step: NonNullable<typeof approval.steps>[number]) => {
    if (typeof step.canAct === 'boolean') return step.canAct;
    if (!user) return false;
    if (step.assigneeUserId === user.id) return true;
    if (user.departmentId && step.assigneeDepartmentId === user.departmentId) return true;
    if (step.assigneeUserId && activeDelegationsToMe.some((d) => d.userId === step.assigneeUserId)) return true;
    return false;
  };

  const earlierStagesComplete = (step: NonNullable<typeof approval.steps>[number]) =>
    !(approval.steps || []).some((s) => s.stage < step.stage && s.status === 'PENDING');

  const isActionable = (step: NonNullable<typeof approval.steps>[number]) => {
    if (typeof step.canAct === 'boolean') {
      return step.canAct && earlierStagesComplete(step);
    }
    return (
      approval.status === 'PENDING' &&
      step.status === 'PENDING' &&
      isAssigned(step) &&
      earlierStagesComplete(step)
    );
  };

  const act = (stepId: number, action: 'approve' | 'reject') => {
    if (busy) return;
    const step = (approval?.steps || []).find((s) => s.id === stepId);
    const kind: CommentPromptActionKind =
      action === 'approve' ? 'approve' : step?.onReject === 'RETURN' ? 'return' : 'reject';
    setError(null);
    setCommentPrompt({ mode: 'step', stepId, action, kind });
  };

  const sourcePath = approvalSourcePath(approval.sourceModule, approval.sourceEntityId, {
    sourceEntityType: approval.sourceEntityType,
    payload: approval.payload,
  });
  const canCancel =
    (approval.status === 'PENDING' || approval.status === 'DRAFT') &&
    !!user &&
    (approval.createdByUserId === user.id || user.role === 'ADMIN');
  const canDelete = !!user && approval.createdByUserId === user.id;
  const canSubmit =
    approval.status === 'DRAFT' &&
    !!user &&
    (approval.createdByUserId === user.id || user.role === 'ADMIN');
  const lastReturn =
    approval.status === 'DRAFT' ? readLastReturn(approval.payload) : null;
  const actionHistory = buildClientActionHistory(approval);

  const openSubmitPrompt = () => {
    if (busy || !canSubmit) return;
    setError(null);
    setCommentPrompt({
      mode: 'submit',
      kind: 'submit',
    });
  };

  const confirmAct = async (comments: string) => {
    if (!commentPrompt) return;
    setBusy(true);
    setError(null);
    try {
      const note = comments || undefined;
      if (commentPrompt.mode === 'submit') {
        await submitApproval(id, note);
      } else if (commentPrompt.stepId != null && commentPrompt.action === 'approve') {
        await approveApprovalStep(id, commentPrompt.stepId, note);
      } else if (commentPrompt.stepId != null) {
        await rejectApprovalStep(id, commentPrompt.stepId, note);
      }
      setCommentPrompt(null);
      await mutate();
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="w-full space-y-5 p-4 md:p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-2">
          <Button type="button" variant="ghost" size="icon" onClick={goBack} aria-label="Back" title="Back">
            <ChevronLeftIcon className="h-5 w-5" />
          </Button>
        </div>
        <div className="flex items-center gap-2">
          {canSubmit && (
            <button
              type="button"
              disabled={busy}
              onClick={openSubmitPrompt}
              className="rounded-xl bg-sky-600 px-3.5 py-2 text-sm font-medium text-white hover:bg-sky-700 disabled:opacity-50"
            >
              {lastReturn ? 'Resubmit for approval' : 'Submit for approval'}
            </button>
          )}
          {canCancel && (
            <button
              type="button"
              disabled={busy}
              onClick={() => setCancelOpen(true)}
              className={cn('rounded-xl border px-3.5 py-2 text-sm font-medium disabled:opacity-50', colors.border, colors.text)}
            >
              Cancel approval
            </button>
          )}
          {canDelete && (
            <button
              type="button"
              disabled={busy}
              onClick={() => setDeleteOpen(true)}
              className="rounded-xl border border-red-200 px-3.5 py-2 text-sm font-medium text-red-600 hover:bg-red-50 disabled:opacity-50 dark:border-red-900/50 dark:hover:bg-red-950/30"
            >
              Delete
            </button>
          )}
        </div>
      </div>

      <CommentPromptDialog
        isOpen={!!commentPrompt}
        onClose={() => {
          if (!busy) setCommentPrompt(null);
        }}
        onConfirm={confirmAct}
        actionKind={commentPrompt?.kind ?? 'approve'}
        title={
          commentPrompt?.mode === 'submit'
            ? lastReturn
              ? 'Resubmit for approval'
              : 'Submit for approval'
            : undefined
        }
        message={
          commentPrompt?.mode === 'submit'
            ? lastReturn
              ? 'Add an optional note for the office about what you changed.'
              : 'Add an optional comment for the reviewers before submitting.'
            : undefined
        }
        confirmText={
          commentPrompt?.mode === 'submit'
            ? lastReturn
              ? 'Resubmit'
              : 'Submit'
            : 'OK'
        }
        cancelText="Cancel"
        isLoading={busy}
      />

      <ConfirmationDialog
        isOpen={cancelOpen}
        onClose={() => {
          if (!busy) setCancelOpen(false);
        }}
        onConfirm={async () => {
          setBusy(true);
          setError(null);
          try {
            await cancelApproval(id);
            setCancelOpen(false);
            await mutate();
          } catch (e) {
            setError(errMsg(e));
          } finally {
            setBusy(false);
          }
        }}
        title="Cancel approval"
        message={`Cancel “${approval.title}”? Pending steps will be skipped and the requester will be notified.`}
        confirmText="Cancel approval"
        cancelText="Keep it"
        confirmVariant="danger"
        isLoading={busy}
      />

      <ConfirmationDialog
        isOpen={deleteOpen}
        onClose={() => {
          if (!busy) setDeleteOpen(false);
        }}
        onConfirm={async () => {
          setBusy(true);
          setError(null);
          try {
            await deleteApproval(id);
            setDeleteOpen(false);
            goBack();
          } catch (e) {
            setError(errMsg(e));
            setBusy(false);
          }
        }}
        title="Delete approval"
        message={
          approval.status === 'DRAFT'
            ? `Delete draft “${approval.title}”? This cannot be undone.`
            : `Delete “${approval.title}” (${statusLabel[approval.status]})? This cannot be undone.`
        }
        confirmText="Delete"
        cancelText="Cancel"
        confirmVariant="danger"
        isLoading={busy}
      />

      {error && (
        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-900/50 dark:bg-red-950/40 dark:text-red-300">
          {error}
        </div>
      )}

      <section className={cn('rounded-2xl border p-5 shadow-sm md:p-6', colors.surface, colors.border)}>
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div className="flex min-w-0 items-start gap-3">
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-sky-50 text-sky-700 dark:bg-sky-950/50 dark:text-sky-300">
              <DocumentTextIcon className="h-5 w-5" />
            </div>
            <div className="min-w-0">
              <h1 className={cn('truncate text-xl font-semibold tracking-tight', colors.text)}>{approval.title}</h1>
              {approval.summary && <p className={cn('mt-1 text-sm', colors.textMuted)}>{approval.summary}</p>}
              <div className="mt-3 flex flex-wrap gap-2 text-xs">
                <MetaChip
                  label="Source"
                  value={`${approval.sourceModule} · ${approval.sourceEntityType}${approval.sourceEntityId ? ` #${approval.sourceEntityId}` : ''}`}
                />
                <MetaChip
                  label="Type"
                  value={approval.approvalType?.name ?? `#${approval.approvalTypeId}`}
                />
                <MetaChip
                  label="Department"
                  value={approval.department?.name || approval.department?.departmentName || '—'}
                />
                {approval.contextKey && <MetaChip label="Context" value={approval.contextKey} />}
              </div>
            </div>
          </div>
          <Badge variant={variants[approval.status]}>{statusLabel[approval.status]}</Badge>
        </div>
        {sourcePath && (
          <Link
            className="mt-4 inline-flex text-sm font-medium text-sky-700 hover:underline dark:text-sky-300"
            to={sourcePath}
          >
            Open source record →
          </Link>
        )}
      </section>

      {lastReturn && (
        <Callout variant="warning" title="Returned for changes">
          <p>
            {[
              lastReturn.byUserName ||
                (lastReturn.byUserId ? `User #${lastReturn.byUserId}` : null),
              formatWhen(lastReturn.at),
              lastReturn.label ? `step “${lastReturn.label}”` : null,
            ]
              .filter(Boolean)
              .join(' · ')}
          </p>
          {lastReturn.comments ? (
            <p className="mt-2 italic">"{lastReturn.comments}"</p>
          ) : (
            <p className="mt-2">No comments were provided with the return.</p>
          )}
          <p className="mt-2">
            Fix the source record, then use <strong>Resubmit for approval</strong> to send it back to the office.
          </p>
        </Callout>
      )}

      {approval.sourceModule === 'documents' && approval.sourceEntityId && (
        <DocumentSourcePreview documentId={approval.sourceEntityId} />
      )}

      <section className={cn('rounded-2xl border p-5 shadow-sm md:p-6', colors.surface, colors.border)}>
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div>
            <h2 className={cn('text-sm font-semibold uppercase tracking-wide', colors.textMuted)}>
              Activity
            </h2>
            <p className={cn('mt-1 text-sm', colors.textMuted)}>
              From approval history (submit, approve, reject, return) with comments.
            </p>
          </div>
          <span className={cn('text-xs tabular-nums', colors.textMuted)}>
            {actionHistory.length} record{actionHistory.length === 1 ? '' : 's'}
          </span>
        </div>

        {actionHistory.length === 0 ? (
          <p className={cn('mt-4 text-sm', colors.textMuted)}>No activity recorded yet.</p>
        ) : (
          <ol className="relative mt-5 space-y-0">
            {actionHistory.map((entry, index) => (
              <li
                key={`${entry.action}-${entry.at}-${entry.stepId ?? index}`}
                className="relative flex gap-3 pb-4 last:pb-0"
              >
                {index < actionHistory.length - 1 ? (
                  <span
                    className="absolute left-[7px] top-4 bottom-0 w-px bg-slate-200 dark:bg-slate-700"
                    aria-hidden
                  />
                ) : null}
                <span
                  className={cn(
                    'relative z-[1] mt-1.5 h-2 w-2 shrink-0 rounded-full ring-2 ring-white dark:ring-slate-900',
                    ACTION_DOT[entry.action] || 'bg-slate-400',
                  )}
                  aria-hidden
                />
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-0.5">
                    <p className={cn('text-sm font-medium', colors.text)}>
                      {ACTION_HISTORY_LABEL[entry.action] || entry.action}
                      {entry.label ? (
                        <span className={cn('font-normal', colors.textMuted)}> · {entry.label}</span>
                      ) : null}
                    </p>
                    <time className={cn('text-xs tabular-nums', colors.textMuted)}>
                      {formatWhen(entry.at) || '—'}
                    </time>
                  </div>
                  <p className={cn('mt-0.5 text-xs', colors.textMuted)}>
                    {entry.byUserName || (entry.byUserId ? `User #${entry.byUserId}` : 'Unknown user')}
                    {entry.stage != null ? ` · stage ${entry.stage}` : ''}
                  </p>
                  {entry.comments?.trim() ? (
                    <p className={cn('mt-1.5 text-sm italic leading-relaxed', colors.text)}>
                      "{entry.comments.trim()}"
                    </p>
                  ) : null}
                </div>
              </li>
            ))}
          </ol>
        )}
      </section>

      <ApprovalFlowPreview
        approval={approval}
        layout="wide"
        busy={busy}
        isActionable={isActionable}
        actingForName={actingForName}
        onAct={act}
      />
    </div>
  );
};
